// Fetch wrapper tipado — factory pattern (v0.2.0)
// Apps resuelven env + pasan baseUrl como arg. Zero cast cross-framework.

export class ApiError extends Error {
  constructor(public status: number, public body: string) {
    super(`API Error ${status}: ${body}`)
    this.name = 'ApiError'
  }
}

export type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown
}

export type ApiClientConfig = {
  /** Base URL del backend (ej. "https://api-crm.ingenios.com.ar") */
  baseUrl: string
  /** Callback para obtener token de auth fresh por request. Return null para skip Authorization header. */
  getAuthToken?: () => string | null
  /** Inject custom fetch (SSR, testing). Default: global `fetch`. */
  fetchFn?: typeof fetch
  /** Default headers merged con per-request headers */
  defaultHeaders?: HeadersInit
}

export type ApiClient = {
  get: <T>(path: string, options?: RequestOptions) => Promise<T>
  post: <T>(path: string, body?: unknown, options?: RequestOptions) => Promise<T>
  put: <T>(path: string, body?: unknown, options?: RequestOptions) => Promise<T>
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) => Promise<T>
  delete: <T>(path: string, options?: RequestOptions) => Promise<T>
}

/**
 * Create ApiClient instance con config injected por la app.
 *
 * @example (Vite app)
 * ```ts
 * export const apiClient = createApiClient({
 *   baseUrl: import.meta.env.VITE_API_URL,
 *   getAuthToken: () => useAuthStore.getState().session?.token ?? null,
 * })
 * ```
 *
 * @example (Next.js app)
 * ```ts
 * export const apiClient = createApiClient({
 *   baseUrl: process.env.NEXT_PUBLIC_API_URL ?? '',
 * })
 * ```
 */
export function createApiClient(config: ApiClientConfig): ApiClient {
  const { baseUrl, getAuthToken, fetchFn = fetch, defaultHeaders } = config

  async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { body, headers, ...rest } = options
    const token = getAuthToken?.()

    const response = await fetchFn(`${baseUrl}${path}`, {
      ...rest,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...defaultHeaders,
        ...headers,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      credentials: 'include', // httpOnly cookie auth (W0-D7)
    })

    if (!response.ok) {
      throw new ApiError(response.status, await response.text())
    }

    return response.json() as Promise<T>
  }

  return {
    get: <T>(path: string, options?: RequestOptions) =>
      request<T>(path, { ...options, method: 'GET' }),
    post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>(path, { ...options, method: 'POST', body }),
    put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>(path, { ...options, method: 'PUT', body }),
    patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>(path, { ...options, method: 'PATCH', body }),
    delete: <T>(path: string, options?: RequestOptions) =>
      request<T>(path, { ...options, method: 'DELETE' }),
  }
}
